"use client";
import React, { useState } from "react";
import MarketCapChart from "../charts/MarketCapChart";
import { useIsMobile } from "@/utils/useIsMobile";
import Link from "next/link"; 

type PortfolioItem = {
  instrument: string;
  holdings: number;
};

type PortfolioResponse = {
  sector: PortfolioItem[];
  company: PortfolioItem[];
  market_cap?: {
    holdings: number;
    instrument: string;
  }[];
};

type tabList = { tabName: string; allocation: number };

type Props = {
  PortfolioData: PortfolioResponse[];
  tabList: tabList[];
  fundSlug: string;
  msoCode: string;
};

export default function MarketCap({ PortfolioData, tabList, fundSlug, msoCode }: Props) {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [activeTabDetail, setActiveTabDetail] = useState<"sectors" | "company">("sectors");

  const isMobile = useIsMobile(); 

  const selectedData = PortfolioData[activeTabIndex];
  const topSectors = selectedData?.sector?.slice(0, 5) || [];
  const topInstruments = selectedData?.company?.slice(0, 5) || [];

  const currentMarketCap = selectedData?.market_cap || [];
  const chartData = currentMarketCap.map((item) => ({
    label: item.instrument,
    value: item.holdings, 
  }));

  const renderList = (items: PortfolioItem[]) =>
    items.map((item, index) => (
      <div key={index} className="mb-4 last-of-type:mb-0">
        <div className="flex justify-between text-base text-[#444444] font-medium">
          <span>{item.instrument}</span>
          <span className="text-primaryBlack">{Number(item.holdings).toFixed(2)}%</span>
        </div>
        <div className="w-full bg-[#C4CFEE66] h-3 rounded-[50px] mt-2">
          <div className="bg-[#657DFF] h-3 rounded-[50px]" style={{ width: `${item.holdings}%` }} />
        </div>
      </div>
    ));

  return (
    <div className="relative bg-white rounded-4xl border px-4 py-5 xs:p-6">
      <div className="border-b border-[#E0E0E0] pb-3">
        <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-5 sm:gap-10 mb-3 sm:mb-5">
          <p className="text-primaryBlack text-xl whitespace-nowrap">Market Cap</p>

          {tabList.length > 0 && (
            <div className="flex max-w-full xs:max-w-fit overflow-x-auto sm:overflow-hidden scrollbar-none mx-auto sm:mr-0 sm:ml-auto p-1 bg-[#ebf3ff] border border-[#1A51AA24] rounded-[100px]">
              {tabList.map((label, index) => (
                <button key={label.tabName} onClick={() => setActiveTabIndex(index)} className={`text-sm rounded-[100px] w-fit shrink-0 sm:shrink font-bold px-2 sm:px-5 py-1.5 capitalize transition-colors ${activeTabIndex === index ? "bg-white text-primaryBlue" : "text-[#202020A6]"}`}>
                  {label.tabName}: <span className="font-semibold text-xs sm:text-sm">{Number(label.allocation).toFixed(2)}%</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <MarketCapChart totalValue={chartData.reduce((acc, cur) => acc + cur.value, 0)} data={chartData} chartName={tabList[activeTabIndex]?.tabName || ""} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
        {isMobile && (
          <div className="flex max-w-fit mx-auto sm:ml-auto sm:mb-5 p-1 bg-[#ebf3ff] border border-[#1A51AA24] rounded-[100px]">
            {["sectors", "company"].map((label) => (
              <button key={label} onClick={() => setActiveTabDetail(label as "sectors" | "company")} className={`text-sm rounded-[100px] font-bold px-3 sm:px-5 py-1.5 capitalize transition-colors ${activeTabDetail === label ? "bg-white text-primaryBlue" : "text-[#202020A6]"}`}>
                {label === "sectors" ? "Top Sectors" : "Top Companies"}
              </button>
            ))}
          </div>
        )}

        {isMobile ? (
          <div className="flex flex-col justify-start gap-4">
            <div className="text-xl text-primaryBlack">{activeTabDetail === "sectors" ? "Top Sectors" : "Top Companies"}</div>
            {activeTabDetail === "sectors" ? renderList(topSectors) : renderList(topInstruments)}
            <Link href={`/mutual-fund/${fundSlug}/portfolio-details/${msoCode}`} className="relative text-center inline-block text-primaryBlue text-base font-medium mt-auto after:content-[''] after:inline-block after:transition-all after:duration-350 after:absolute after:left-0 after:right-0 after:bottom-0 after:mx-auto after:h-px after:w-0 after:bg-primaryBlue hover:after:w-full">
              View All
            </Link>
          </div>
        ) : (
          <>
            <div className="flex flex-col justify-start gap-4">
              <div className="text-xl text-primaryBlack">Top Sectors</div>
              {renderList(topSectors)}
              <Link href={`/mutual-fund/${fundSlug}/portfolio-details/${msoCode}`} className="relative max-w-fit text-center inline-block text-primaryBlue text-base font-medium mt-auto after:content-[''] after:inline-block after:transition-all after:duration-350 after:absolute after:left-0 after:right-0 after:bottom-0 after:mx-auto after:h-px after:w-0 after:bg-primaryBlue hover:after:w-full">
                View All
              </Link>
            </div>
            <div className="flex flex-col justify-start gap-4">
              <div className="text-xl text-primaryBlack">Top Companies</div>
              {renderList(topInstruments)}
              <Link href={`/mutual-fund/${fundSlug}/portfolio-details/${msoCode}`} className="relative max-w-fit text-center inline-block text-primaryBlue text-base font-medium mt-auto after:content-[''] after:inline-block after:transition-all after:duration-350 after:absolute after:left-0 after:right-0 after:bottom-0 after:mx-auto after:h-px after:w-0 after:bg-primaryBlue hover:after:w-full">
                View All
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
